/**
 * Inventory's own drawings: a glyph per *kind of thing*, for an item with no
 * photo yet, and the few interface marks the shelf needs (grid, list, chevron,
 * camera). Same 24-grid and 1.6 stroke as `icons.tsx`, so they sit beside the
 * navigation set without looking borrowed.
 *
 * Kept here rather than in `icons.tsx` because these are pictures of parts,
 * not places in the app.
 */

const PATHS: Readonly<Record<string, string>> = {
  camera:
    'M4 8.5A1.5 1.5 0 015.5 7h2.2l1.4-2h5.8l1.4 2h2.2A1.5 1.5 0 0120 8.5v9a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 014 17.5zM12 16.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z',
  sensor:
    'M12 13.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM8.5 15.5a5 5 0 010-7M15.5 8.5a5 5 0 010 7M5.6 18.4a9 9 0 010-12.8M18.4 5.6a9 9 0 010 12.8',
  fastener: 'M7 4.5h10l-1 3H8zM9 7.5v10.5l3 2 3-2V7.5M9 10.5l6-1M9 13.5l6-1M9 16.5l6-1',
  cable: 'M9 3.5v4M15 3.5v4M7 7.5h10v3a5 5 0 01-10 0zM12 15.5v2a3 3 0 003 3h3',
  battery:
    'M5 7.5h12.5A1.5 1.5 0 0119 9v6a1.5 1.5 0 01-1.5 1.5H5A1.5 1.5 0 013.5 15V9A1.5 1.5 0 015 7.5zM21 10.5v3M11.5 9.5l-2 3h3l-2 3',
  motor:
    'M12 14.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM12 17.5a5.5 5.5 0 100-11 5.5 5.5 0 000 11zM12 3.5v3M12 17.5v3M3.5 12h3M17.5 12h3M6 6l2.1 2.1M15.9 15.9L18 18M6 18l2.1-2.1M15.9 8.1L18 6',
  board:
    'M7.5 7.5h9v9h-9zM10 10h4v4h-4zM9.5 4v3.5M14.5 4v3.5M9.5 16.5V20M14.5 16.5V20M4 9.5h3.5M4 14.5h3.5M16.5 9.5H20M16.5 14.5H20',
  assembly: 'M12 3.5l7.5 4.2v8.6L12 20.5 4.5 16.3V7.7zM12 12l7.5-4.3M12 12v8.5M12 12L4.5 7.7',
  robot:
    'M9 4h6M12 4v3M6.5 7h11A1.5 1.5 0 0119 8.5v6a1.5 1.5 0 01-1.5 1.5h-11A1.5 1.5 0 015 14.5v-6A1.5 1.5 0 016.5 7zM9.5 10.5v1.5M14.5 10.5v1.5M8 16v2M16 16v2',
  wheel: 'M12 20.5a8.5 8.5 0 100-17 8.5 8.5 0 000 17zM12 14.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM12 3.5v6M12 14.5v6M3.5 12h6M14.5 12h6',
  bearing: 'M12 20.5a8.5 8.5 0 100-17 8.5 8.5 0 000 17zM12 15a3 3 0 100-6 3 3 0 000 6zM12 6v.1M12 17.9v.1M6 12h.1M17.9 12h.1',
  tool: 'M14.5 4.5a4 4 0 00-4.9 5.2L4.5 14.8a1.8 1.8 0 002.6 2.6l5.1-5.1a4 4 0 005.2-4.9l-2.4 2.4-2.1-.5-.5-2.1z',
  box: 'M12 3.5l8 4v9l-8 4-8-4v-9zM4 7.5l8 4 8-4M12 11.5v9M8 5.5l8 4',
  // Interface marks.
  grid: 'M4.5 4.5h6v6h-6zM13.5 4.5h6v6h-6zM4.5 13.5h6v6h-6zM13.5 13.5h6v6h-6z',
  list: 'M9 6.5h10.5M9 12h10.5M9 17.5h10.5M4.5 6.5h1M4.5 12h1M4.5 17.5h1',
  chevron: 'M9.5 6l6 6-6 6',
  back: 'M14.5 6l-6 6 6 6',
  plus: 'M12 5v14M5 12h14',
  tag: 'M4 12.6V5.5A1.5 1.5 0 015.5 4h7.1l7.4 7.4a1.5 1.5 0 010 2.1l-6.5 6.5a1.5 1.5 0 01-2.1 0zM8.5 8.5v.1',
  truck: 'M3.5 6.5h10v9h-10zM13.5 9.5h4l3 3v3h-7M7 18.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM17 18.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z',
  shelf: 'M4 9.5h16M4 14.5h16M4 5.5h16v13H4zM9.5 5.5v13M14.5 5.5v13',
}

export type GlyphName = keyof typeof PATHS

export function Glyph({ name, size = 16 }: { name: GlyphName; size?: number }): JSX.Element {
  return (
    <svg
      className="inv-glyph"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name] ?? PATHS.box} />
    </svg>
  )
}

/**
 * Which drawing stands in for an item with no photo. Matched on words in its
 * category and name, because both are free text an engineer typed — a lookup
 * keyed on exact category names would go stale the first time one is renamed.
 * Anything unrecognised is a box, which is what most deliveries are anyway.
 */
const WORDS: readonly (readonly [GlyphName, RegExp])[] = [
  ['robot', /\brobot/],
  ['camera', /camera|lens|vision|stereo/],
  ['sensor', /sensor|lidar|imu|encoder|probe|radar/],
  ['fastener', /screw|bolt|nut\b|washer|fastener|rivet|hardware|standoff/],
  ['cable', /cable|wire|harness|connector|plug|lead\b/],
  ['battery', /batter|cell\b|power|charger|psu/],
  ['motor', /motor|actuator|servo|drive|gearbox|gear\b/],
  ['board', /board|pcb|electronic|controller|compute|chip|module|computer/],
  ['wheel', /wheel|tyre|tire|caster|track\b/],
  ['bearing', /bearing|bushing|seal|o-ring/],
  ['tool', /tool|jig|fixture/],
  ['assembly', /assembl|frame|chassis|bracket|housing/],
]

export function glyphFor(category: string, name = ''): GlyphName {
  // The name first: a "harness" filed under "hardware" is a cable, not a screw.
  for (const hay of [name.toLowerCase(), category.toLowerCase()]) {
    for (const [glyph, words] of WORDS) if (words.test(hay)) return glyph
  }
  return 'box'
}

/** One of five tints on the brand's cyan–violet axis, stable for a category. */
export function toneFor(category: string, name = ''): number {
  const key = (category || name).toLowerCase()
  // FNV-1a: short, similar names ("sensor", "drive") still land apart.
  let h = 0x811c9dc5
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 0x01000193) >>> 0
  return h % 5
}
